import { PageInfo } from '@/types';

import clsx from 'clsx';
import React, { useMemo } from 'react';
import { tv, VariantProps } from 'tailwind-variants';

const calculatePageInfo = (
  currentPage: number,
  perPage: number,
  totalCount: number,
  pageRange: number
) => {
  if (totalCount === 0)
    return { currentPage: 1, pageNumbers: [1], lastPage: 1 };

  const lastPage = Math.ceil(totalCount / perPage);
  let startPage = Math.max(1, currentPage - Math.floor(pageRange / 2));
  const endPage = Math.min(lastPage, startPage + pageRange - 1);
  // endPageの計算後にstartPageを再計算
  startPage = Math.max(1, endPage - pageRange + 1);

  const pageNumbers = [];
  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  return { currentPage, pageNumbers, lastPage };
};

const button = tv({
  base: 'grid place-content-center rounded-full',
  variants: {
    type: {
      normal: '',
    },
    disabled: {
      true: 'pointer-events-none',
    },
    size: {
      md: 'size-10',
      sm: 'size-7 text-sm',
    },
  },
  compoundVariants: [
    {
      // normal disable button
      type: 'normal',
      disabled: true,
      className: 'border border-r-0 border-teal-600 bg-teal-600 text-white',
    },
    {
      // normal not disable button
      type: 'normal',
      disabled: false,
      className: 'transition-colors duration-150 hover:text-teal-900',
    },
  ],
});

const ButtonItem = ({
  label,
  onClick,
  disabled,
  size,
  type,
}: {
  label: React.ReactElement | number;
} & Pick<React.HTMLProps<HTMLButtonElement>, 'onClick'> &
  VariantProps<typeof button>) => (
  <li>
    <button
      className={button({ type, size, disabled })}
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </button>
  </li>
);

type PagerProps = {
  pageInfo: PageInfo;
  pageRange?: number;
  className?: string;
  onClick: (pageStart: number) => void;
  size?: VariantProps<typeof button>['size'];
};

export const Pagination = ({
  pageInfo,
  pageRange = 5,
  size = 'md',
  className,
  onClick,
}: PagerProps) => {
  const { pageNumbers, lastPage } = useMemo(
    () =>
      calculatePageInfo(
        pageInfo.currentPage,
        pageInfo.perPage,
        pageInfo.totalCount,
        pageRange
      ),
    [pageInfo.currentPage, pageRange, pageInfo.perPage, pageInfo.totalCount]
  );

  return (
    <ul className={clsx('inline-flex gap-x-5 text-teal-600', className)}>
      {pageNumbers[0] !== 1 && (
        <>
          <ButtonItem
            type="normal"
            size={size}
            label={1}
            disabled={false}
            onClick={() => onClick(0)}
          />
          {pageNumbers[0] > 2 && <li className="flex items-center">...</li>}
        </>
      )}
      {pageNumbers.map((pageNumber) => (
        <ButtonItem
          key={pageNumber}
          type="normal"
          size={size}
          label={pageNumber}
          disabled={pageNumber === pageInfo.currentPage}
          onClick={() => onClick((pageNumber - 1) * pageInfo.perPage)}
        />
      ))}
      {pageNumbers.slice(-1)[0] !== lastPage && (
        <>
          {pageNumbers.slice(-1)[0] < lastPage - 1 && (
            <li className="flex items-center">...</li>
          )}
          <ButtonItem
            type="normal"
            size={size}
            label={lastPage}
            disabled={false}
            onClick={() => onClick((lastPage - 1) * pageInfo.perPage)}
          />
        </>
      )}
    </ul>
  );
};
